import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, BellOff, Activity, Menu } from 'lucide-react';
import StudioSwitcher from '@/components/shell/StudioSwitcher';
import PersonaSwitcher from '@/components/shell/PersonaSwitcher';
import HelpLauncher from '@/components/shell/HelpLauncher';
import PersistenceIndicator from '@/components/shell/PersistenceIndicator';
import ConnectionIndicator from '@/components/shell/ConnectionIndicator';
import SimClockStepper from '@/components/shell/SimClockStepper';
import { demoControls } from '@/config/featureFlags';
import { useTourStore } from '@/state/tourStore';

function formatSimClock(iso: string) {
  const d = new Date(iso);
  const opts: Intl.DateTimeFormatOptions = {
    timeZone: 'Australia/Melbourne',
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  return new Intl.DateTimeFormat('en-AU', opts).format(d);
}

export default function TopBar() {
  const navigate = useNavigate();
  // Task 15: the reporting screen has its own StudioCompareFilter in its
  // filter bar, so the top-bar StudioSwitcher is a redundant second studio
  // control on /reporting (and /reporting-legacy) — hide it there.
  const hideStudioSwitcher = useLocation().pathname.startsWith('/reporting');
  const clockIso = useSimulationStore((s) => s.clockIso);
  const muted = useUIStore((s) => s.muted);
  const railCollapsed = useUIStore((s) => s.railCollapsed);
  const toggleRail = useUIStore((s) => s.toggleRail);
  const toggleActivityFeed = useUIStore((s) => s.toggleActivityFeed);
  const setMuted = useUIStore((s) => s.setMuted);
  const demoEnabled = useTourStore((s) => s.enabled);
  const setDemoEnabled = useTourStore((s) => s.setEnabled);

  return (
    <header className="sticky top-0 z-30 bg-ink-deep border-b border-gold-dim/20 h-16 px-6 flex items-center gap-6">
      <button
        type="button"
        onClick={toggleRail}
        className="btn-quiet px-2"
        data-testid="rail-toggle"
        aria-label={railCollapsed ? 'Expand navigation' : 'Collapse navigation'}
        aria-expanded={!railCollapsed}
        title={railCollapsed ? 'Show menu' : 'Hide menu'}
      >
        <Menu size={18} />
      </button>

      <button
        type="button"
        onClick={() => navigate('/')}
        className="flex items-center"
        data-testid="brand-mark"
        aria-label="Tattoo Vault — home"
      >
        <span className="font-empera text-xl text-gold-bright uppercase tracking-widest whitespace-nowrap shrink-0">
          Tattoo Vault
        </span>
      </button>

      {demoControls ? (
        <SimClockStepper />
      ) : (
        <div
          className="flex items-center gap-3 px-3 py-1.5 rounded-full border border-gold-dim/30"
          data-testid="sim-clock"
          title="Studio time"
        >
          <span className="font-mono text-sm text-gold-bright tabular-nums">
            {formatSimClock(clockIso)}
          </span>
        </div>
      )}

      <div className="flex-1" />

      <div className="flex items-center gap-3">
        <ConnectionIndicator />
        <PersistenceIndicator />
        {hideStudioSwitcher ? null : <StudioSwitcher />}
        <HelpLauncher />
        <button
          type="button"
          onClick={() => setDemoEnabled(!demoEnabled)}
          data-testid="demo-toggle"
          aria-pressed={demoEnabled}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-inter uppercase tracking-widest transition ${
            demoEnabled
              ? 'border-gold-bright/60 bg-gold/10 text-gold-bright'
              : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/40'
          }`}
        >
          Training {demoEnabled ? 'on' : 'off'}
        </button>
        <button
          type="button"
          onClick={() => setMuted(!muted)}
          className="btn-quiet"
          data-testid="audio-toggle"
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <BellOff size={14} /> : <Bell size={14} />}
        </button>
        <button
          type="button"
          onClick={toggleActivityFeed}
          className="btn-quiet"
          data-testid="activity-feed-toggle"
        >
          <Activity size={14} /> Feed
        </button>
        <PersonaSwitcher />
      </div>
    </header>
  );
}
